import { Component, useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  Archive,
  Boxes,
  CalendarDays,
  CalendarPlus,
  CalendarClock,
  Camera,
  ChevronRight,
  CircleDollarSign,
  ClipboardList,
  FileHeart,
  Handshake,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  Menu,
  MessageSquareHeart,
  PackageCheck,
  Pencil,
  Pill,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Stethoscope,
  Ticket,
  Truck,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import {
  APP_NAME,
  CLINIC_ID,
  configurationError,
  supabase,
} from "./supabase.js";
import {
  loadActionFormData,
  loadDashboard,
  loadDentalFormData,
  loadEstetikFormData,
  loadDokterUmumFormData,
  loadExaminationFormData,
  loadPackageReferenceData,
  loadPageData,
  loadPaymentFormData,
  loadProcurementData,
  loadRegistrationFormData,
  reopenVisitForCorrection,
  resolveIdentity,
  recordCheckIn,
  recordCheckOut,
  createSurveyToken,
} from "./data.js";
import { applyAppearance } from "./lib/appearance.js";
import { canonicalDivision, divisionLabel } from "./lib/division.js";
import {
  ageFromBirthDate,
  dateLabel,
  friendlyError,
  number,
  rupiah,
} from "./lib/format.js";
import { PatientFormModal, VisitFormModal } from "./RegistrationForms.jsx";
import { ExaminationFormModal } from "./ExaminationForm.jsx";
import { EstetikFormModal } from "./EstetikForm.jsx";
import { DentalFormModal } from "./DentalForm.jsx";
import { DokterUmumFormModal } from "./DokterUmumForm.jsx";
import { PhotoModal } from "./PhotoModal.jsx";
import { ActionFormModal } from "./ActionForm.jsx";
import { PaymentFormModal } from "./PaymentForm.jsx";
import { PackagePurchaseModal } from "./PackagePurchaseForm.jsx";
import { DailyClosingPage } from "./DailyClosingPage.jsx";
import { ProcurementPage } from "./ProcurementPage.jsx";
import { CancellationForm } from "./CancellationForm.jsx";
import { MedicalRecordsPage } from "./MedicalRecordsPage.jsx";
import { MasterTindakanPage } from "./MasterTindakanPage.jsx";
import { MasterBHPPage } from "./MasterBHPPage.jsx";
import { PackagesPage } from "./PackagesPage.jsx";
import { PiutangPage } from "./PiutangPage.jsx";
import { PenggunaPage } from "./PenggunaPage.jsx";
import { PayrollPage } from "./PayrollPage.jsx";
import { KehadiranPage } from "./KehadiranPage.jsx";
import { AppointmentPage } from "./AppointmentPage.jsx";
import { InformedConsentModal } from "./InformedConsentModal.jsx";
import { FollowUpPage } from "./FollowUpPage.jsx";
import { SurveyPage } from "./SurveyPage.jsx";
import { SurveyDashboard } from "./SurveyDashboard.jsx";
import { VoucherClaimPage } from "./VoucherClaimPage.jsx";
import { MemberGetMemberPage } from "./MemberGetMemberPage.jsx";
import { MembershipPage } from "./MembershipPage.jsx";
import { SettingsPage } from "./SettingsPage.jsx";
import { CancellationButton } from "./CancellationButton";
import { PatientPortalPage } from "./PatientPortalPage.jsx";
import FarmasiPage from "./FarmasiPage.jsx";

const MENU = {
  dashboard: { label: "Dashboard", icon: LayoutDashboard },
  followup: { label: "Follow-Up", icon: CalendarClock },
  registration: { label: "Pendaftaran", icon: CalendarDays },
  nakesboard: { label: "Pemeriksaan", icon: Stethoscope },
  payments: { label: "Pembayaran", icon: CircleDollarSign },
  piutang:  { label: "Piutang", icon: CircleDollarSign },
  payroll: { label: "Payroll", icon: CircleDollarSign },
  survey: { label: "Survey", icon: MessageSquareHeart },
  voucher: { label: "Klaim Voucher", icon: Ticket },
  memberMgm: { label: "Membership", icon: Handshake },
  packages: { label: "Paket", icon: PackageCheck },
  medicalRecords: { label: "Rekam Medis", icon: FileHeart },
  clients: { label: "Pasien", icon: UsersRound },
  services: { label: "Layanan", icon: ClipboardList },
  bhp: { label: "Inventory", icon: Boxes },
  // procurement dipindah ke dalam tab Inventory
  farmasi: { label: "Farmasi", icon: Pill },
  attendance: { label: "Kehadiran", icon: Activity },
  reports: { label: "Laporan", icon: Archive },
  settings: { label: "Pengaturan", icon: Settings },
  // users, backup, roles masuk ke dalam Pengaturan
};

const LIVE_PAGES = new Set([
  "dashboard",
  "registration",
  "nakesboard",
  "payments",
  "packages",
  "medicalRecords",
  "clients",
  "bhp",
]);
const DATA_PAGES = new Set([
  "registration",
  "nakesboard",
  "payments",
  "packages",
  "clients",
]);
const FUNCTIONAL_PAGES = new Set([
  ...LIVE_PAGES,
  "reports",
  "services",
  "payroll",
  "attendance",
  "appointment",
  "followup",
  "settings",
  "survey",
  "voucher",
  "memberMgm",
  "farmasi",
  "piutang",
  // procurement, users, backup, roles sudah masuk ke dalam halaman lain
]);

class PageErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (!this.state.error) return this.props.children;
    return <div className="alert alert-danger"><strong>Modul tidak dapat dibuka.</strong> {this.state.error?.message || 'Terjadi kesalahan saat memuat modul.'}</div>;
  }
}

function statusTone(value) {
  const text = String(value || "").toUpperCase();
  if (/LUNAS|PAID|SELESAI|ACTIVE|FINAL/.test(text)) return "positive";
  if (/BATAL|CANCEL|SUSPEND|INACTIVE/.test(text)) return "danger";
  if (/BELUM|UNPAID|DAFTAR|DRAFT|MENUNGGU|PENDING|READY/.test(text))
    return "warning";
  if (/^PARTIAL$/.test(text)) return "info";
  return "neutral";
}

function SatusehatBadge({ row }) {
  const status = row.satusehat_patient_status || "";
  const labelMap = {
    FOUND: "IHS ditemukan",
    MULTIPLE_FOUND: "Perlu pilih IHS",
    NOT_FOUND: "Tidak ditemukan",
    NOT_FOUND_SANDBOX: "Sandbox: tidak ada",
    ERROR: "Error"
  };
  const styleMap = {
    FOUND: { background: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
    MULTIPLE_FOUND: { background: "#fef3c7", color: "#b45309", border: "#fde68a" },
    NOT_FOUND: { background: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
    NOT_FOUND_SANDBOX: { background: "#e0f2fe", color: "#0369a1", border: "#bae6fd" },
    ERROR: { background: "#fee2e2", color: "#b91c1c", border: "#fecaca" }
  };
  const style = styleMap[status] || { background: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" };
  const label = row.satusehat_consent ? (labelMap[status] || "Belum dicek") : "Tidak consent";

  return (
    <div className="primary-cell">
      <span
        title={row.satusehat_patient_error || row.satusehat_patient_id || ""}
        style={{
          alignSelf: "flex-start",
          background: style.background,
          border: `1px solid ${style.border}`,
          borderRadius: 6,
          color: style.color,
          fontSize: 11,
          fontWeight: 800,
          lineHeight: 1.2,
          padding: "3px 8px",
          whiteSpace: "nowrap"
        }}
      >
        {label}
      </span>
      <span>
        {row.satusehat_patient_id
          || (row.satusehat_patient_checked_at ? dateLabel(row.satusehat_patient_checked_at) : "Belum ada IHS")}
      </span>
    </div>
  );
}

function Status({ children }) {
  return (
    <span className={`status status-${statusTone(children)}`}>
      {children || "—"}
    </span>
  );
}

function Brand({ compact = false, subtitle }) {
  return (
    <div className={`brand ${compact ? "brand-compact" : ""}`}>
      <div className="brand-mark">XV</div>
      {!compact && (
        <div>
          <strong>{APP_NAME}</strong>
          {subtitle && <span>{subtitle}</span>}
        </div>
      )}
    </div>
  );
}

function LoadingScreen({ label = "Memuat portal XV…" }) {
  return (
    <main className="center-screen">
      <Brand />
      <div className="loader" aria-label="Memuat" />
      <p>{label}</p>
    </main>
  );
}

function Login({ onSignedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(configurationError);

  async function submit(event) {
    event.preventDefault();
    if (!supabase || configurationError) return;
    setBusy(true);
    setError("");
    const result = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setBusy(false);
    if (result.error) return setError(friendlyError(result.error));
    onSignedIn(result.data.session);
  }

  return (
    <main className="login-shell">
      <section className="login-story">
        <Brand />
        <div className="login-message">
          <span className="eyebrow">Portal Operasional XV</span>
          <h1>Data klinik yang rapi, aman, dan siap dipakai.</h1>
          <p>
            Tahap validasi Supabase untuk memastikan data pasien, kunjungan,
            rekam medis, transaksi, dan inventory XV tampil sesuai sumber lama.
          </p>
          <div className="safety-note">
            <ShieldCheck size={20} />
            <span>
              Alur klinis, pembayaran, inventory & procurement XV aktif
            </span>
          </div>
        </div>
      </section>

      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <span className="eyebrow">Masuk ke {CLINIC_ID}</span>
          <h2>Selamat datang kembali</h2>
          <p>Gunakan email dan kata sandi Supabase Auth.</p>
          {error && <div className="alert alert-danger">{error}</div>}
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="nama@jarumdewa.id"
              autoComplete="email"
              required
            />
          </label>
          <label>
            Kata sandi
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Masukkan kata sandi"
              autoComplete="current-password"
              minLength={8}
              required
            />
          </label>
          <button
            className="button button-primary button-block"
            disabled={busy || !!configurationError}
          >
            {busy ? "Memeriksa akses…" : "Masuk ke Portal XV"}
          </button>
          <small>
            Service role key tidak pernah dipakai atau disimpan di browser.
          </small>
        </form>
      </section>
    </main>
  );
}

function authActionFromLocation() {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const queryParams = new URLSearchParams(window.location.search);
  const type = hashParams.get("type") || queryParams.get("type");
  return type === "invite" || type === "recovery" ? type : null;
}

function PasswordSetup({ session, action, onComplete, onSignOut }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault();
    if (password.length < 8) {
      setError("Kata sandi minimal 8 karakter.");
      return;
    }
    if (password !== confirmation) {
      setError("Konfirmasi kata sandi belum sama.");
      return;
    }
    setBusy(true);
    setError("");
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (updateError) {
      setError(friendlyError(updateError));
      return;
    }
    window.history.replaceState({}, document.title, window.location.pathname);
    onComplete(session);
  }

  return (
    <main className="login-shell">
      <section className="login-story">
        <Brand />
        <div className="login-message">
          <span className="eyebrow">Akses aman {CLINIC_ID}</span>
          <h1>Password Anda, kendali Anda.</h1>
          <p>
            Buat kata sandi pribadi untuk mengakses portal. Admin klinik tidak
            dapat melihat kata sandi ini.
          </p>
          <div className="safety-note">
            <ShieldCheck size={20} />
            <span>Link ini hanya digunakan untuk pengaturan akses akun.</span>
          </div>
        </div>
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <LockKeyhole size={28} color="#2f6f4f" />
          <span className="eyebrow">{action === "invite" ? "Undangan akses" : "Atur ulang akses"}</span>
          <h2>Buat kata sandi</h2>
          <p>{session?.user?.email || "Akun klinik"}</p>
          {error && <div className="alert alert-danger">{error}</div>}
          <label>
            Kata sandi baru
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>
          <label>
            Ulangi kata sandi
            <input
              type="password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>
          <button className="button button-primary button-block" disabled={busy}>
            {busy ? "Menyimpan…" : "Simpan dan masuk"}
          </button>
          <button type="button" className="button button-quiet button-block" onClick={onSignOut}>
            Batalkan
          </button>
        </form>
      </section>
    </main>
  );
}

// Map key lama (disimpan sebelum sinkronisasi) ke key MENU yang benar
const LEGACY_KEY_MAP = {
  payment:    'payments',
  rekam_medis:'medicalRecords',
  pasien:     'clients',
  paket:      'packages',
  laporan:    'reports',
  kehadiran:  'attendance',
};

function Sidebar({ identity, current, onSelect, open, onClose }) {
  const rawMenu = Array.isArray(identity.role.menu_access)
    ? identity.role.menu_access
    : [];
  const apotikActive = !!identity?.activeModules?.apotik;
  const menuOrder = Object.keys(MENU);
  const menu = Array.from(new Set(
    rawMenu
      .map((k) => LEGACY_KEY_MAP[k] || k)
      .filter((key) => MENU[key])
      .filter((key) => key !== 'survey' && key !== 'voucher')
      .filter((key) => key !== 'farmasi' || apotikActive)
  )).sort((a, b) => menuOrder.indexOf(a) - menuOrder.indexOf(b));
  return (
    <>
      {open && (
        <button
          className="sidebar-scrim"
          aria-label="Tutup menu"
          onClick={onClose}
        />
      )}
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`}>
        <div className="sidebar-head">
          <Brand subtitle={identity?.clinic?.name} />
          <button
            className="icon-button sidebar-close"
            onClick={onClose}
            aria-label="Tutup menu"
          >
            <X size={21} />
          </button>
        </div>
        <div className="readonly-badge">
          <ShieldCheck size={15} /> Alur klinis XV aktif
        </div>
        <nav>
          {menu.map((key) => {
            const item = MENU[key];
            const Icon = item.icon;
            return (
              <button
                key={key}
                className={`nav-item ${current === key ? "nav-item-active" : ""}`}
                onClick={() => {
                  onSelect(key);
                  onClose();
                }}
              >
                <Icon size={20} />
                <span>{item.label}</span>
                {!FUNCTIONAL_PAGES.has(key) && (
                  <span className="nav-soon">berikutnya</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-user">
          <div className="avatar">
            {identity.appUser.name.slice(0, 1).toUpperCase()}
          </div>
          <div>
            <strong>{identity.appUser.name}</strong>
            <span>
              {identity.role.role_name} · {identity.clinic.code}
            </span>
          </div>
        </div>
      </aside>
    </>
  );
}

function Topbar({ title, identity, onMenu, onRefresh, refreshing, onSignOut }) {
  return (
    <header className="topbar">
      <div className="topbar-title">
        <button
          className="icon-button mobile-only"
          onClick={onMenu}
          aria-label="Buka menu"
        >
          <Menu size={22} />
        </button>
        <div>
          <span>{identity.clinic.name}</span>
          <h1>{title}</h1>
        </div>
      </div>
      <div className="topbar-actions">
        <button
          className="button button-quiet"
          onClick={onRefresh}
          disabled={refreshing}
        >
          <RefreshCw size={17} className={refreshing ? "spin" : ""} />
          <span className="desktop-only">Muat ulang</span>
        </button>
        <button className="button button-dark" onClick={onSignOut}>
          <LogOut size={17} />
          <span className="desktop-only">Keluar</span>
        </button>
      </div>
    </header>
  );
}

function Metric({ label, value, note, icon, tone = "" }) {
  const Icon = icon;
  return (
    <article className={`metric ${tone}`}>
      <div className="metric-icon">
        <Icon size={22} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{note}</small>
      </div>
    </article>
  );
}

function Empty({ children = "Belum ada data." }) {
  return (
    <div className="empty">
      <Archive size={28} />
      <p>{children}</p>
    </div>
  );
}

function Dashboard({ data, onNavigate, onRefresh  }) {
    const readyForPayment = (data.visits || []).filter(
    (v) => v.payment_status === 'SIAP_BAYAR'
  );
  return (
    <div className="page-stack">
      <section className="welcome-card">
        <div>
          <span className="eyebrow">Ringkasan {dateLabel(data.today)}</span>
          <h2>Operasional XV hari ini</h2>
          <p>
            Data dibaca langsung dari Supabase dan dibatasi oleh RLS tenant XV.
          </p>
        </div>
        <div className="validation-pill">
          <ShieldCheck size={18} /> Migrasi XV terhubung
        </div>
      </section>

      <section className="metrics-grid">
        <Metric
          label="Total pasien"
          value={number.format(data.clientCount)}
          note="Master pasien XV"
          icon={UsersRound}
        />
        <Metric
          label="Kunjungan hari ini"
          value={number.format(data.visits.length)}
          note="Semua status"
          icon={CalendarDays}
        />
        <Metric
          label="Menunggu pembayaran"
          value={number.format(data.waitingPayment)}
          note="Belum lunas"
          icon={CircleDollarSign}
          tone="metric-warn"
        />
        <Metric
          label="Penerimaan hari ini"
          value={
            data.revenue === null
              ? "Disembunyikan"
              : rupiah.format(data.revenue)
          }
          note={
            data.revenue === null
              ? "Sesuai hak akses role"
              : `${data.transactions.length} transaksi`
          }
          icon={Activity}
          tone="metric-green"
        />
      </section>

      <section className="dashboard-grid">
        <article className="panel panel-wide">
          <div className="panel-head">
            <div>
              <span className="eyebrow">Antrean</span>
              <h3>Kunjungan hari ini</h3>
            </div>
            <button
              className="text-button"
              onClick={() => onNavigate("registration")}
            >
              Lihat semua <ChevronRight size={16} />
            </button>
          </div>
          {data.visits.length ? (
            <div className="compact-list">
              {data.visits.slice(0, 12).map((row) => {
                const div = canonicalDivision(row.division);
                const poliMeta =
                  div === 'DENTAL'      ? { label: 'Poli Gigi',   color: '#0369a1', bg: '#e0f2fe' } :
                  div === 'ESTETIK'     ? { label: 'Poli Estetik', color: '#7c3aed', bg: '#f5f3ff' } :
                  div === 'DOKTER_UMUM' ? { label: 'Poli Umum',   color: '#16a34a', bg: '#f0fdf4' } :
                  div === 'WELLNESS'    ? { label: 'Wellness / TCM', color: '#15803d', bg: '#f0fdf4' } :
                                          { label: divisionLabel(row.division), color: '#6b7280', bg: '#f3f4f6' };
                return (
                  <div className="compact-row" key={row.id}>
                    <div className="avatar avatar-soft" style={{ background: poliMeta.bg, color: poliMeta.color, fontWeight: 700 }}>
                      {(row.client?.name || "?").slice(0, 1)}
                    </div>
                    <div className="grow">
                      <strong>{row.client?.name || row.client_id}</strong>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span className="queue-poli-badge" style={{ background: poliMeta.bg, color: poliMeta.color }}>
                          {poliMeta.label}
                        </span>
                        {row.visit_number}
                        {row.doctor_name ? ` · ${row.doctor_name}` : ''}
                      </span>
                    </div>
                    <Status>{row.visit_status}</Status>
                    <CancellationButton
                      visit={row}
                      onSuccess={() => onRefresh()}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty>Belum ada kunjungan hari ini.</Empty>
          )}
        </article>

        <article className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">SIAP BAYAR</span>
              <h3>Menunggu Pembayaran</h3>
            </div>
          </div>
          {readyForPayment.length ? (
            <div className="compact-list">
              {readyForPayment.map((visit) => {
                const div = canonicalDivision(visit.division);
                const poliMeta =
                  div === 'DENTAL'      ? { label: 'Poli Gigi',    color: '#0369a1', bg: '#e0f2fe' } :
                  div === 'ESTETIK'     ? { label: 'Poli Estetik', color: '#7c3aed', bg: '#f5f3ff' } :
                  div === 'DOKTER_UMUM' ? { label: 'Poli Umum',    color: '#16a34a', bg: '#f0fdf4' } :
                  div === 'WELLNESS'    ? { label: 'Wellness / TCM', color: '#15803d', bg: '#f0fdf4' } :
                                          { label: divisionLabel(visit.division), color: '#6b7280', bg: '#f3f4f6' };
                return (
                  <div key={visit.id} className="compact-row">
                    <div className="avatar avatar-soft" style={{ background: poliMeta.bg, color: poliMeta.color, fontWeight: 700 }}>
                      {(visit.client?.name || '?').slice(0, 1)}
                    </div>
                    <div className="grow">
                      <strong>{visit.client?.name || visit.client_id}</strong>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className="queue-poli-badge" style={{ background: poliMeta.bg, color: poliMeta.color }}>{poliMeta.label}</span>
                        {visit.visit_number}
                      </span>
                    </div>
                    <button
                      className="button button-mini button-primary"
                      onClick={() => onNavigate("payments")}
                    >
                      <CircleDollarSign size={14} /> Proses Bayar
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty>Tidak ada yang menunggu pembayaran.</Empty>
          )}
        </article>

        {data.lowStock.length > 0 && (
          <article className="panel panel-alert">
            <div className="panel-head">
              <div>
                <span className="eyebrow">Inventory</span>
                <h3>Stok perlu perhatian</h3>
              </div>
            </div>
            <div className="compact-list">
              {data.lowStock.slice(0, 5).map((row) => (
                <div className="compact-row" key={row.id}>
                  <div className="grow">
                    <strong>{row.name}</strong>
                    <span>
                      Minimum {number.format(row.minimum_stock)} {row.unit}
                    </span>
                  </div>
                  <strong className="danger-text">
                    {number.format(row.current_stock)} {row.unit}
                  </strong>
                </div>
              ))}
            </div>
          </article>
        )}
      </section>
    </div>
  );
}

function PageHeading({ page, search, onSearch, actions }) {
  const placeholders = {
    clients: "Ketik minimal 2 huruf nama / telepon / kode pasien",
    registration: "Cari pasien atau nomor kunjungan…",
    nakesboard: "Cari pasien atau status…",
    payments: "Cari nomor invoice…",
    packages: "Cari pasien, paket, atau invoice…",
    medicalRecords: "Cari isi keluhan…",
    bhp: "Cari item, kode, atau kategori…",
  };
  return (
    <section className="page-heading">
      <div>
        <span className="eyebrow">Validasi data XV</span>
        <h2>{MENU[page]?.label}</h2>
        <p>
          {page === "clients"
            ? "Agar aplikasi ringan, menu Pasien tidak memuat semua pasien otomatis. Cari pasien lama lalu klik Edit untuk mengubah data."
            : page === "registration"
            ? "Data dibatasi hak akses role dan seluruh perubahan dicatat."
            : "Menampilkan maksimal 150 data terbaru."}
        </p>
      </div>
      <div className="page-heading-tools">
        <label className="search-box">
          <Search size={18} />
          <input
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={placeholders[page]}
          />
        </label>
        {actions}
      </div>
    </section>
  );
}

function DataTable({ columns, rows, keyField = "id", getExpandedContent = null }) {
  if (!rows.length) return <Empty>Data tidak ditemukan.</Empty>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const expanded = getExpandedContent ? getExpandedContent(row) : null;
            return (
              <>
                <tr key={row[keyField]}>
                  {columns.map((column) => (
                    <td key={column.key}>
                      {column.render ? column.render(row) : row[column.key] || "—"}
                    </td>
                  ))}
                </tr>
                {expanded ? (
                  <tr key={row[keyField] + "__expanded"} className="dt-expanded-row">
                    <td colSpan={columns.length} style={{ padding: 0 }}>
                      {expanded}
                    </td>
                  </tr>
                ) : null}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CollectionPage({ page, rows, search, onSearch, identity, onChanged, onNavigate }) {
  const canWritePatients = identity.permissions.has("patients.write");
  const canWriteVisits = identity.permissions.has("visits.write");
  const canWriteMedical = identity.permissions.has("medical_records.write");
  const canWriteActions = identity.permissions.has("clinical_actions.write");
  const canReadBilling = identity.permissions.has("billing.read");
  const canWriteBilling = identity.permissions.has("billing.write");
  const canKoreksiSelesai = /^(admin|kasir)$/i.test(identity.role.role_name || "");

  // Divisi nakes yang login
  // - isAdminRole (admin/kasir/frontliner): tidak dibatasi divisi
  // - staffMember ada tapi division null/kosong: pakai WELLNESS (divisi default)
  // - staff non-admin tanpa staffMember: tidak mendapat antrean lintas divisi
  const isAdminRole = /^(admin|kasir|frontliner)$/i.test(identity.role.role_name || '');
  const nakedDiv = identity.staffMember
    ? canonicalDivision(identity.staffMember.division || 'WELLNESS')
    : isAdminRole
      ? 'ALL'
      : '';
  // Apakah nakes ini boleh mengisi SOAP untuk divisi kunjungan tertentu?
  const canHandleDiv = useCallback((visitDiv) => {
    if (isAdminRole || nakedDiv === 'ALL') return true;
    if (!nakedDiv) return false;
    return nakedDiv === canonicalDivision(visitDiv);
  }, [isAdminRole, nakedDiv]);
  const [dialog, setDialog] = useState(null);
  const [references, setReferences] = useState(null);
  const [examinationData, setExaminationData] = useState(null);
  const [estetikData, setEstetikData] = useState(null);
  const [dentalData, setDentalData] = useState(null);
  const [dokterUmumData, setDokterUmumData] = useState(null);
  const [actionData, setActionData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  // Expandable rincian tindakan di tabel pembayaran
  const [expandedTxId, setExpandedTxId] = useState(null);
  const [expandedTxItems, setExpandedTxItems] = useState({});
  const [packageReferences, setPackageReferences] = useState(null);
  const [dialogError, setDialogError] = useState("");
  const [notice, setNotice] = useState("");
  const [selectedPaymentIds, setSelectedPaymentIds] = useState(() => new Set());
  const [lunasDate, setLunasDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [lunasRows, setLunasRows] = useState(null);
  const [lunasLoading, setLunasLoading] = useState(false);
  const [lunasError, setLunasError] = useState("");
  const [koreksiNotice, setKoreksiNotice] = useState("");
  const [surveyInfo, setSurveyInfo] = useState(null); // { token, phone, name, waUrl }
  const [surveyLoadingId, setSurveyLoadingId] = useState(null); // visit_id sedang di-fetch

  // Auto-load 10 transaksi lunas terbaru saat halaman payments dibuka
  useEffect(() => {
    if (page === "payments") loadLunasHistory(false);
  }, [page]); // eslint-disable-line

  const togglePaymentExpand = useCallback(async (txId) => {
    if (expandedTxId === txId) { setExpandedTxId(null); return; }
    setExpandedTxId(txId);
    if (!expandedTxItems[txId]) {
      try {
        const db = supabase;
        const res = await db
          .from('transaction_items')
          .select('id,clinic_id,service_id,list_price,net_price,item_status,notes')
          .eq('transaction_id', txId);
        if (res.error) throw res.error;
        const rawItems = (res.data || []).filter(
          (i) => !/^CANCELLED$/i.test(i.item_status || '')
        );
        const serviceIds = [...new Set(rawItems.map((item) => item.service_id).filter(Boolean))];
        let serviceMap = {};
        if (serviceIds.length) {
          const servicesRes = await db
            .from('services')
            .select('id,name')
            .eq('clinic_id', rawItems[0]?.clinic_id || CLINIC_ID)
            .in('id', serviceIds);
          if (servicesRes.error) throw servicesRes.error;
          serviceMap = Object.fromEntries((servicesRes.data || []).map((service) => [service.id, service.name]));
        }
        const items = rawItems.map((item) => ({
          ...item,
          service_name: serviceMap[item.service_id] || '',
        }));
        setExpandedTxItems((prev) => ({ ...prev, [txId]: items }));
      } catch {
        setExpandedTxItems((prev) => ({ ...prev, [txId]: [] }));
      }
    }
  }, [expandedTxId, expandedTxItems]);

  async function openDialog(type, record = null) {
    setDialog({ type, record, loading: true });
    setDialogError("");
    try {
      if (type === "examination") {
        const nextExaminationData = await loadExaminationFormData(record.id);
        setExaminationData(nextExaminationData);
        setDialog({ type, record, loading: false });
        return;
      }
      if (type === "estetik") {
        const nextEstetikData = await loadEstetikFormData(record.id);
        setEstetikData(nextEstetikData);
        setDialog({ type, record, loading: false });
        return;
      }
      if (type === "dental") {
        const nextDentalData = await loadDentalFormData(record.id);
        setDentalData(nextDentalData);
        setDialog({ type, record, loading: false });
        return;
      }
      if (type === "dokter_umum") {
        const nextDokterUmumData = await loadDokterUmumFormData(record.id);
        setDokterUmumData(nextDokterUmumData);
        setDialog({ type, record, loading: false });
        return;
      }
      if (type === "photo") {
        // no async load needed — visit + client already on record
        setDialog({ type, record, loading: false });
        return;
      }
      if (type === "action") {
        const nextActionData = await loadActionFormData(record.id);
        setActionData(nextActionData);
        setDialog({ type, record, loading: false });
        return;
      }
      if (type === "payment") {
        const nextPaymentData = await loadPaymentFormData(record.id);
        setPaymentData(nextPaymentData);
        setDialog({ type, record, loading: false });
        return;
      }
      if (type === "packagePurchase") {
        const nextPackageReferences = await loadPackageReferenceData();
        setPackageReferences(nextPackageReferences);
        setDialog({ type, record, loading: false });
        return;
      }
      const nextReferences = await loadRegistrationFormData();
      setReferences(nextReferences);
      setDialog({ type, record, loading: false });
    } catch (loadError) {
      setDialogError(friendlyError(loadError));
      setDialog(null);
    }
  }

  function handleSaved(_result, message, finalized = false) {
    const dialogType = dialog?.type;
    // Snapshot paymentData sebelum dialog di-clear
    const snapshotPayment = paymentData;
    setDialog(null);
    setNotice(message);
    // Hanya navigate ke Pembayaran jika finalisasi TINDAKAN (bukan SOAP)
    if (finalized && page === "nakesboard" && dialogType === "action") {
      onNavigate("payments");
    } else if (dialogType === "payment") {
      // Payment: tunggu DB commit sebelum refresh
      setTimeout(() => onChanged(), 500);
      // Generate survey token & siapkan WA link (pakai paymentData yg sudah load lengkap)
      const client  = snapshotPayment?.client;
      const visit   = snapshotPayment?.visit;
      if (client || visit) {
        const clientName  = client?.name  || "";
        const clientPhone = client?.phone || "";
        const visitId     = visit?.id || "";
        const clientId    = client?.id || "";
        createSurveyToken({ visit_id: visitId, client_id: clientId, client_name: clientName, client_phone: clientPhone })
          .then(({ token }) => {
            const surveyUrl = `${window.location.origin}${window.location.pathname}?survey=${token}`;
            const phone = clientPhone.replace(/\D/g, "").replace(/^0/, "62");
            const msg = encodeURIComponent(
              `Halo ${clientName || "Kak"}! 😊\n\nTerima kasih sudah berkunjung ke klinik kami hari ini.\n\nMohon luangkan 1 menit untuk mengisi survei kepuasan:\n${surveyUrl}\n\n⏰ *Isi sebelum link kadaluwarsa* dan dapatkan voucher spesial untuk kunjungan berikutnya! 🎁`
            );
            const waUrl = phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
            setSurveyInfo({ token, phone: clientPhone, name: clientName, waUrl, surveyUrl });
          })
          .catch((e) => console.error("[Survey] createSurveyToken failed:", e));
      }
    } else {
      onChanged();
    }
  }

  function togglePaymentSelect(id) {
    setSelectedPaymentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function movePaymentToExamination(row) {
    const visitStatus = row?.visit?.visit_status || "";
    if (!row?.visit_id) {
      setKoreksiNotice("Kunjungan tidak ditemukan untuk transaksi ini.");
      return;
    }
    if (!canKoreksiSelesai) {
      setKoreksiNotice("Koreksi dari Pembayaran hanya bisa dilakukan oleh Admin atau Kasir.");
      return;
    }
    try {
      await reopenVisitForCorrection(row.visit_id);
      setSelectedPaymentIds(new Set());
      setKoreksiNotice("");
      setNotice(`Kunjungan ${row.visit?.visit_number || row.visit_id} dikembalikan ke Pemeriksaan dengan status BELUM_BAYAR.`);
      onNavigate("nakesboard");
    } catch (err) {
      setKoreksiNotice(`Gagal membuka koreksi dari status ${visitStatus || "kunjungan"}: ${friendlyError(err)}`);
    }
  }

  function handleKoreksiTerpilih() {
    if (!selectedPaymentIds.size) return;
    if (selectedPaymentIds.size === 1) {
      const id = Array.from(selectedPaymentIds)[0];
      const row = (rows || []).find((r) => r.id === id);
      movePaymentToExamination(row);
      return;
    }
    setKoreksiNotice(
      "Koreksi tindakan hanya bisa dilakukan satu kunjungan per waktu. Klik tombol \"Koreksi\" pada baris yang dituju.",
    );
  }

  async function loadLunasHistory(filterByDate = false) {
    setLunasLoading(true);
    setLunasError("");
    try {
      let query = supabase
        .from("transactions")
        .select(
          "id,invoice_number,client_id,transaction_date,net_amount,amount_received,payment_status,payment_method,visit_id",
        )
        .eq("clinic_id", CLINIC_ID)
        .in("payment_status", ["LUNAS", "PAID", "TERBAYAR"])
        .order("transaction_date", { ascending: false });
      if (filterByDate) {
        const start = `${lunasDate}T00:00:00`;
        const end = `${lunasDate}T23:59:59`;
        query = query.gte("transaction_date", start).lte("transaction_date", end).limit(150);
      } else {
        query = query.limit(10);
      }
      const { data: txRows, error: txError } = await query;
      if (txError) throw txError;

      const clientIds = Array.from(
        new Set((txRows || []).map((r) => r.client_id).filter(Boolean)),
      );
      const visitIds = Array.from(
        new Set((txRows || []).map((r) => r.visit_id).filter(Boolean)),
      );

      let clientMapLocal = {};
      if (clientIds.length) {
        const { data: clientRows } = await supabase
          .from("clients")
          .select("id, name, client_code, phone")
          .in("id", clientIds);
        (clientRows || []).forEach((c) => {
          clientMapLocal[c.id] = c;
        });
      }

      let visitMapLocal = {};
      if (visitIds.length) {
        const { data: visitRows } = await supabase
          .from("visits")
          .select("id, visit_number, room_name")
          .in("id", visitIds);
        (visitRows || []).forEach((v) => {
          visitMapLocal[v.id] = v;
        });
      }

      setLunasRows(
        (txRows || []).map((r) => ({
          ...r,
          client: clientMapLocal[r.client_id] || null,
          visit: visitMapLocal[r.visit_id] || null,
        })),
      );
    } catch (err) {
      setLunasError(friendlyError(err));
    } finally {
      setLunasLoading(false);
    }
  }

  // Helper: label jenis transaksi untuk display (berdasarkan payment_method)
  async function openSurveyWa(row) {
    if (!row.visit_id || surveyLoadingId) return;
    setSurveyLoadingId(row.visit_id);
    try {
      // Cari token yg sudah ada, atau buat baru
      let token;
      const { data: existing } = await supabase
        .from("survey_tokens")
        .select("token")
        .eq("visit_id", row.visit_id)
        .maybeSingle();
      if (existing?.token) {
        token = existing.token;
      } else {
        const result = await createSurveyToken({
          visit_id: row.visit_id,
          client_id: row.client_id,
          client_name: row.client?.name || "",
          client_phone: row.client?.phone || "",
        });
        token = result.token;
      }
      const surveyUrl = `${window.location.origin}${window.location.pathname}?survey=${token}`;
      const clientName = row.client?.name || "Kak";
      const rawPhone = (row.client?.phone || "").replace(/\D/g, "").replace(/^0/, "62");
      if (!rawPhone) {
        alert(`Nomor WA ${clientName} tidak tersedia di data klien.\n\nLink survey:\n${surveyUrl}`);
        setSurveyLoadingId(null);
        return;
      }
      const msg = encodeURIComponent(
        `Halo ${clientName}! 😊\n\nTerima kasih sudah berkunjung ke klinik kami.\n\nMohon luangkan 1 menit untuk mengisi survei kepuasan:\n${surveyUrl}\n\n⏰ *Isi sebelum link kadaluwarsa* dan dapatkan voucher spesial untuk kunjungan berikutnya! 🎁`
      );
      window.open(`https://wa.me/${rawPhone}?text=${msg}`, "_blank");
    } catch (e) {
      console.error("[Survey WA]", e);
      alert("Gagal membuat link survey. Coba lagi.");
    } finally {
      setSurveyLoadingId(null);
    }
  }

  function lunasJenisLabel(r) {
    const m = (r.payment_method || "").toLowerCase();
    if (m.includes("token")) return { label: "Penggunaan Token Paket", color: "#1a7f5a" };
    if (m.includes("paket")) return { label: "Pembelian Paket", color: "#2563eb" };
    if (m.includes("potong") || m.includes("gaji")) return { label: "Potong Gaji", color: "#7c3aed" };
    if (m.includes("promo")) return { label: "Promo", color: "#d97706" };
    if (m.includes("campuran") || m.includes("mixed")) return { label: "Campuran", color: "#0891b2" };
    if (m.includes("qris")) return { label: "QRIS", color: "#374151" };
    if (m.includes("cash") || m.includes("tunai")) return { label: "Cash", color: "#374151" };
    return { label: r.payment_method || "Cash / QRIS", color: "#374151" };
  }

  // Helper: hitung summary stats dari lunasRows
  function lunasSummary(rows) {
    let uangMasuk = 0, potongGaji = 0, paket = 0, token = 0, campuran = 0, promo = 0;
    (rows || []).forEach((r) => {
      const j = lunasJenisLabel(r);
      if (j.label === "Penggunaan Token Paket") token++;
      else if (j.label === "Pembelian Paket") paket++;
      else if (j.label === "Potong Gaji") potongGaji += Number(r.amount_received || 0);
      else if (j.label === "Campuran") { campuran++; uangMasuk += Number(r.amount_received || 0); }
      else if (j.label === "Promo") promo++;
      else uangMasuk += Number(r.amount_received || 0);
    });
    return { uangMasuk, potongGaji, paket, token, campuran, promo };
  }

  const columns = useMemo(() => {
    if (page === "clients") {
      const result = [
        { key: "client_code", label: "Kode" },
        {
          key: "name",
          label: "Nama pasien",
          render: (row) => (
            <div className="primary-cell">
              <strong>{row.name}</strong>
              <span>{row.phone || "Tanpa nomor telepon"}</span>
            </div>
          ),
        },
        { key: "gender", label: "JK" },
        {
          key: "satusehat_patient_status",
          label: "SATUSEHAT",
          render: (row) => <SatusehatBadge row={row} />,
        },
        {
          key: "birth_date",
          label: "Usia",
          render: (row) => ageFromBirthDate(row.birth_date),
        },
        { key: "client_category", label: "Kategori" },
        {
          key: "updated_at",
          label: "Diperbarui",
          render: (row) => dateLabel(row.updated_at),
        },
      ];
      if (canWritePatients)
        result.push({
          key: "actions",
          label: "Aksi",
          render: (row) => (
            <button
              className="button button-mini button-quiet"
              onClick={() => openDialog("patient", row)}
            >
              <Pencil size={14} /> Edit
            </button>
          ),
        });
      return result;
    }
    if (page === "registration" || page === "nakesboard") {
      const result = [
        { key: "visit_number", label: "Kunjungan" },
        {
          key: "client_id",
          label: "Pasien",
          render: (row) => (
            <div className="primary-cell">
              <strong>{row.client?.name || row.client_id}</strong>
              <span>{row.client?.client_code || ""}</span>
            </div>
          ),
        },
        {
          key: "visit_date",
          label: "Tanggal",
          render: (row) => dateLabel(row.visit_date),
        },
        { key: "doctor_name", label: "Nakes / Dokter" },
        {
          key: "visit_status",
          label: "Status",
          render: (row) => <Status>{row.visit_status}</Status>,
        },
        ...(page === "nakesboard"
          ? [
              {
                key: "medical_note_status",
                label: "Rekam medis",
                render: (row) => (
                  <Status>{row.medical_note_status || "BELUM ADA"}</Status>
                ),
              },
            ]
          : []),
        {
          key: "payment_status",
          label: "Pembayaran",
          render: (row) => <Status>{row.payment_status}</Status>,
        },
      ];
      if (page === "nakesboard" && (canWriteMedical || canWriteActions))
        result.push({
          key: "actions",
          label: "Aksi",
          render: (row) => {
            const isTerdaftar   = /^TERDAFTAR$/i.test(row.visit_status || "");
            const isPemeriksaan = /^PEMERIKSAAN$/i.test(row.visit_status || "");
            const isTindakan    = /^TINDAKAN$/i.test(row.visit_status || "");
            const isEstetik    = /^ESTETIK$/i.test(row.division || "");
            const isDental     = /^DENTAL$/i.test(row.division || "");
            const isDokterUmum = /^DOKTER_UMUM$/i.test(row.division || "");
            const soapDialog = isEstetik ? "estetik" : isDental ? "dental" : isDokterUmum ? "dokter_umum" : "examination";
            const soapLabel  = isEstetik ? "SOAP Estetik" : isDental ? "SOAP Dental" : isDokterUmum ? "SOAP Dokter Umum" : "Isi SOAP";
            const consentSigned = row.consent_status === "SIGNED";
            const consentDraft  = row.consent_status === "DRAFT";

            // Nakes hanya boleh buka SOAP / Tindakan untuk divisi sendiri
            const allowedToAct = canHandleDiv(row.division);

            const consentBtn = (
              <button
                className={`button button-mini ${consentSigned ? "button-consent-signed" : "button-quiet"}`}
                onClick={() => openDialog("consent", row)}
                title={consentSigned ? "Consent sudah ditandatangani" : consentDraft ? "Form consent ada, belum difoto" : "Buat & cetak informed consent"}
              >
                {consentSigned ? "✓ Consent" : consentDraft ? "⚠ Consent" : "Consent"}
              </button>
            );

            // TERDAFTAR → isi SOAP + Consent + Foto (Estetik)
            if (isTerdaftar && canWriteMedical)
              return (
                <div className="row-actions">
                  {allowedToAct ? (
                    <button
                      className="button button-mini button-primary"
                      onClick={() => openDialog(soapDialog, row)}
                    >
                      <Stethoscope size={14} /> {soapLabel}
                    </button>
                  ) : (
                    null
                  )}
                  {consentBtn}
                  {isEstetik && allowedToAct && (
                    <button
                      className="button button-mini button-quiet"
                      onClick={() => openDialog("photo", row)}
                    >
                      <Camera size={14} /> Foto
                    </button>
                  )}
                </div>
              );
            // PEMERIKSAAN → SOAP sudah dibuka; jika FINAL → Buka Tindakan, jika belum → lanjut SOAP
            if (isPemeriksaan) {
              const soapFinal = /^(FINAL|AMENDED)$/i.test(row.medical_note_status || "");
              if (soapFinal && canWriteActions)
                return (
                  <div className="row-actions">
                    {allowedToAct ? (
                      <button
                        className="button button-mini button-primary"
                        onClick={() => openDialog("action", row)}
                      >
                        <Activity size={14} /> Buka Tindakan
                      </button>
                    ) : (
                      null
                    )}
                    {consentBtn}
                    {isEstetik && allowedToAct && (
                      <button
                        className="button button-mini button-quiet"
                        onClick={() => openDialog("photo", row)}
                      >
                        <Camera size={14} /> Foto
                      </button>
                    )}
                  </div>
                );
              if (!soapFinal && canWriteMedical)
                return (
                  <div className="row-actions">
                    {allowedToAct ? (
                      <button
                        className="button button-mini button-primary"
                        onClick={() => openDialog(soapDialog, row)}
                      >
                        <Stethoscope size={14} /> Lanjut SOAP
                      </button>
                    ) : (
                      null
                    )}
                    {consentBtn}
                  </div>
                );
            }
            // TINDAKAN → buka tindakan + Consent + Foto (Estetik) + Jadwalkan Kembali
            if (isTindakan && canWriteActions)
              return (
                <div className="row-actions">
                  {allowedToAct ? (
                    <button
                      className="button button-mini button-primary"
                      onClick={() => openDialog("action", row)}
                    >
                      <Activity size={14} /> Buka Tindakan
                    </button>
                  ) : (
                    null
                  )}
                  {consentBtn}
                  {isEstetik && allowedToAct && (
                    <button
                      className="button button-mini button-quiet"
                      onClick={() => openDialog("photo", row)}
                    >
                      <Camera size={14} /> Foto
                    </button>
                  )}
                  <button
                    className="button button-mini button-quiet"
                    onClick={() => onNavigate("followup")}
                    title="Jadwalkan follow-up untuk pasien ini"
                  >
                    <CalendarPlus size={14} /> Jadwalkan
                  </button>
                </div>
              );
            return <span className="muted">Tidak ada akses</span>;
          },
        });
      return result;
    }
    if (page === "payments") {
      const result = [
        {
          key: "select",
          label: "",
          render: (row) => (
            <input
              type="checkbox"
              checked={selectedPaymentIds.has(row.id)}
              onChange={() => togglePaymentSelect(row.id)}
            />
          ),
        },
        {
          key: "visit_number",
          label: "No Visit",
          render: (row) =>
            row.visit?.visit_number || "—",
        },
        {
          key: "client_id",
          label: "Klien",
          render: (row) => row.client?.name || row.client_id,
        },
        {
          key: "room_name",
          label: "Ruang",
          render: (row) => row.visit?.room_name || "—",
        },
        {
          key: "transaction_date",
          label: "Tanggal",
          render: (row) => dateLabel(row.transaction_date),
        },
        {
          key: "net_amount",
          label: "Tagihan Normal",
          render: (row) => rupiah.format(Number(row.net_amount || 0)),
        },
      ];
      if (canReadBilling || canWriteBilling)
        result.push({
          key: "actions",
          label: "Aksi",
          render: (row) => {
            const ready = /^(READY_FOR_PAYMENT|SIAP_BAYAR)$/i.test(
              row.payment_status || "",
            );
            const isExpanded = expandedTxId === row.id;
            return (
              <div className="row-actions">
                <button
                  className="button button-mini button-quiet"
                  title={isExpanded ? "Tutup rincian tindakan" : "Lihat rincian tindakan"}
                  onClick={() => togglePaymentExpand(row.id)}
                  style={{ fontWeight: 700, minWidth: 28 }}
                >
                  <span aria-hidden="true">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                </button>
                <button
                  className={`button button-mini ${ready && canWriteBilling ? "button-primary" : "button-quiet"}`}
                  onClick={() => openDialog("payment", row)}
                >
                  <CircleDollarSign size={14} />{" "}
                  {ready && canWriteBilling ? "Bayar" : "Detail"}
                </button>
                <button
                  className="button button-mini button-quiet"
                  title={
                    row.visit_id
                      ? "Koreksi tindakan untuk kunjungan ini"
                      : "Kunjungan tidak ditemukan"
                  }
                  disabled={!row.visit_id || !canWriteActions}
                  onClick={() => movePaymentToExamination(row)}
                >
                  Koreksi
                </button>
              </div>
            );
          },
        });
      return result;
    }
    if (page === "medicalRecords")
      return [
        {
          key: "created_at",
          label: "Tanggal",
          render: (row) => dateLabel(row.created_at, { time: true }),
        },
        {
          key: "client_id",
          label: "Pasien",
          render: (row) => (
            <div className="primary-cell">
              <strong>{row.client?.name || row.client_id}</strong>
              <span>{row.doctor_name || "—"}</span>
            </div>
          ),
        },
        {
          key: "current_complaint",
          label: "Keluhan",
          render: (row) => (
            <span className="truncate-cell">
              {row.current_complaint || "—"}
            </span>
          ),
        },
        { key: "syndrome_status", label: "Sindrom" },
        {
          key: "draft_status",
          label: "Dokumen",
          render: (row) => <Status>{row.draft_status}</Status>,
        },
      ];
    if (page === "bhp")
      return [
        { key: "item_code", label: "Kode" },
        {
          key: "name",
          label: "Item",
          render: (row) => (
            <div className="primary-cell">
              <strong>{row.name}</strong>
              <span>{row.category || "Tanpa kategori"}</span>
            </div>
          ),
        },
        {
          key: "current_stock",
          label: "Stok",
          render: (row) => (
            <strong
              className={
                Number(row.current_stock) <= Number(row.minimum_stock)
                  ? "danger-text"
                  : ""
              }
            >
              {number.format(row.current_stock)} {row.unit}
            </strong>
          ),
        },
        {
          key: "division",
          label: "Divisi",
          render: (row) => <Status>{row.division || "ALL"}</Status>,
        },
        {
          key: "minimum_stock",
          label: "Minimum",
          render: (row) => `${number.format(row.minimum_stock)} ${row.unit}`,
        },
        {
          key: "target_stock",
          label: "Target",
          render: (row) => `${number.format(row.target_stock)} ${row.unit}`,
        },
        {
          key: "status",
          label: "Status",
          render: (row) => <Status>{row.status}</Status>,
        },
      ];
    return [];
  }, [
    page,
    canWritePatients,
    canWriteMedical,
    canWriteActions,
    canReadBilling,
    canWriteBilling,
    canKoreksiSelesai,
    canHandleDiv,
    expandedTxId,
    onNavigate,
    selectedPaymentIds,
    togglePaymentExpand,
  ]);

  const actions =
    page === "registration" || page === "clients" || page === "packages" ? (
      <div className="heading-actions">
        {page === "registration" && canWritePatients ? (
          <button
            className="button button-quiet"
            onClick={() => openDialog("patient")}
          >
            <Plus size={17} /> Pasien Baru
          </button>
        ) : null}
        {page === "registration" && canWriteVisits ? (
          <button
            className="button button-primary"
            onClick={() => openDialog("visit")}
          >
            <CalendarPlus size={17} /> Buat Kunjungan
          </button>
        ) : null}
      </div>
    ) : null;

  const displayRows = (() => {
    const baseRows = rows || [];
    if (page === "payments") {
      return baseRows.filter((r) =>
        /^(READY_FOR_PAYMENT|SIAP_BAYAR)$/i.test(r.payment_status || ""),
      );
    }
    if (page === "nakesboard") {
      return baseRows.filter((r) => canHandleDiv(r.division));
    }
    return baseRows;
  })();
  const isClientSearchReady = page !== "clients" || search.trim().length >= 2;

  return (
    <div className="page-stack">
      {page !== "packages" ? (
        <PageHeading
          page={page}
          search={search}
          onSearch={onSearch}
          actions={actions}
        />
      ) : null}
      {dialogError ? (
        <div className="alert alert-danger">{dialogError}</div>
      ) : null}
      {notice ? (
        <div className="alert alert-success" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ flex: 1 }}><strong>Berhasil.</strong> {notice}</span>
          {surveyInfo && (
            <a
              href={surveyInfo.waUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "#25d366", color: "#fff", borderRadius: 8,
                padding: "5px 14px", fontSize: 13, fontWeight: 700,
                textDecoration: "none", whiteSpace: "nowrap",
              }}
            >
              📲 Kirim Survey WA
            </a>
          )}
          <button className="alert-close" onClick={() => { setNotice(""); setSurveyInfo(null); }}>×</button>
        </div>
      ) : null}
      {koreksiNotice ? (
        <div className="alert alert-info">
          {koreksiNotice}
          <button className="alert-close" onClick={() => setKoreksiNotice("")}>
            ×
          </button>
        </div>
      ) : null}
      {page !== "packages" ? <section className="panel table-panel">
        {isClientSearchReady ? (
          <>
            <div className="table-summary">
              <strong>
                {page === "payments" ? "Daftar Pasien Siap Bayar" : ""}
              </strong>
              {page === "payments" ? (
                <button
                  className="button button-quiet"
                  disabled={!selectedPaymentIds.size}
                  onClick={handleKoreksiTerpilih}
                >
                  Koreksi Terpilih ({selectedPaymentIds.size})
                </button>
              ) : (
                <>
                  <strong>{number.format(displayRows.length)}</strong>
                  <span>baris ditampilkan</span>
                </>
              )}
            </div>
            <DataTable
              columns={columns}
              rows={displayRows}
              getExpandedContent={page === "payments" ? (row) => {
                if (expandedTxId !== row.id) return null;
                const items = expandedTxItems[row.id];
                const rupiah = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 });
                return (
                  <div className="payment-rincian-panel">
                    <div className="payment-rincian-title">Rincian Tindakan</div>
                    {!items ? (
                      <p className="payment-rincian-loading">Memuat…</p>
                    ) : items.length === 0 ? (
                      <p className="payment-rincian-empty">Belum ada item tindakan. Buka draft pemeriksaan, pilih Perawatan, lalu simpan/finalkan ulang.</p>
                    ) : (
                      <table className="payment-rincian-table">
                        <thead>
                          <tr>
                            <th>Tindakan / Layanan</th>
                            <th>Qty</th>
                            <th>Harga Normal</th>
                            <th>Harga Bayar</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item) => (
                            <tr key={item.id}>
                              <td>
                                {item.service_name || 'Tindakan'}
                                {item.notes ? <small style={{ display: 'block', color: '#64748b', marginTop: 2 }}>{item.notes}</small> : null}
                              </td>
                              <td style={{ textAlign: 'center' }}>1</td>
                              <td style={{ textAlign: 'right' }}>{rupiah.format(Number(item.list_price || 0))}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: Number(item.net_price) === 0 ? '#16a34a' : '#111827' }}>
                                {Number(item.net_price) === 0 ? 'Paket / Token' : rupiah.format(Number(item.net_price || 0))}
                              </td>
                              <td>
                                <span style={{
                                  fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                                  background: /^(FINAL|DONE|SELESAI)$/i.test(item.item_status) ? '#dcfce7' : '#f3f4f6',
                                  color: /^(FINAL|DONE|SELESAI)$/i.test(item.item_status) ? '#15803d' : '#6b7280',
                                }}>{item.item_status || '—'}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              } : null}
            />
          </>
        ) : (
          <Empty>Belum ada data ditampilkan. Silakan cari pasien.</Empty>
        )}
      </section> : null}
      {page === "payments" ? (
        <section className="panel">
          <div className="panel-head">
            <div>
              <h3>Riwayat Lunas</h3>
              <p className="muted">
                {lunasRows && lunasRows.length < 10
                  ? `Menampilkan ${lunasRows.length} transaksi terbaru.`
                  : "Menampilkan 10 transaksi terbaru."}{" "}
                Filter per tanggal lalu klik "Refresh Riwayat".
              </p>
            </div>
            <div className="row-actions">
              <input
                type="date"
                value={lunasDate}
                onChange={(e) => setLunasDate(e.target.value)}
              />
              <button
                className="button button-primary"
                onClick={() => loadLunasHistory(true)}
                disabled={lunasLoading}
              >
                {lunasLoading ? "Memuat…" : "Refresh Riwayat"}
              </button>
            </div>
          </div>
          {lunasError ? (
            <div className="alert alert-danger">{lunasError}</div>
          ) : null}
          {(() => {
            if (lunasRows === null) return <p className="muted" style={{ padding: "12px 0" }}>Memuat riwayat...</p>;
            const stats = lunasSummary(lunasRows);
            return (
              <>
                {/* Summary stats bar */}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", padding: "10px 0 14px", borderBottom: "1px solid #e5e7eb", marginBottom: 12 }}>
                  {[
                    { label: "Total Riwayat", val: lunasRows.length, fmt: "count" },
                    { label: "Uang Masuk", val: stats.uangMasuk, fmt: "rp" },
                    { label: "Potong Gaji", val: stats.potongGaji, fmt: "rp" },
                    { label: "Paket", val: stats.paket, fmt: "count" },
                    { label: "Token", val: stats.token, fmt: "count" },
                    { label: "Campuran", val: stats.campuran, fmt: "count" },
                    { label: "Promo", val: stats.promo, fmt: "count" },
                  ].map(({ label, val, fmt }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 10, background: "#f9fafb", borderRadius: 8, padding: "6px 14px", border: "1px solid #e5e7eb", minWidth: 100 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 1 }}>{label}</div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>
                          {fmt === "rp" ? rupiah.format(val) : val}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Table */}
                {lunasRows.length ? (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
                          <th style={{ padding: "6px 10px", color: "#6b7280", fontWeight: 600 }}>Jenis</th>
                          <th style={{ padding: "6px 10px", color: "#6b7280", fontWeight: 600 }}>Tanggal</th>
                          <th style={{ padding: "6px 10px", color: "#6b7280", fontWeight: 600 }}>Invoice</th>
                          <th style={{ padding: "6px 10px", color: "#6b7280", fontWeight: 600 }}>Survey</th>
                          <th style={{ padding: "6px 10px", color: "#6b7280", fontWeight: 600 }}>Klien</th>
                          <th style={{ padding: "6px 10px", color: "#6b7280", fontWeight: 600 }}>Metode</th>
                          <th style={{ padding: "6px 10px", color: "#6b7280", fontWeight: 600, textAlign: "right" }}>Total</th>
                          <th style={{ padding: "6px 10px", color: "#6b7280", fontWeight: 600, textAlign: "center" }}>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lunasRows.map((r) => {
                          const jenis = lunasJenisLabel(r);
                          const tgl = r.transaction_date ? r.transaction_date.slice(0, 10) : "—";
                          return (
                            <tr key={r.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                              <td style={{ padding: "8px 10px" }}>
                                <span style={{
                                  display: "inline-block",
                                  background: jenis.color + "18",
                                  color: jenis.color,
                                  border: `1px solid ${jenis.color}40`,
                                  borderRadius: 5,
                                  fontSize: 12,
                                  padding: "2px 8px",
                                  fontWeight: 600,
                                }}>
                                  {jenis.label}
                                </span>
                              </td>
                              <td style={{ padding: "8px 10px", fontFamily: "monospace", color: "#374151" }}>{tgl}</td>
                              <td style={{ padding: "8px 10px", color: "#374151" }}>{r.invoice_number || "—"}</td>
                              <td style={{ padding: "8px 10px" }}>
                                <div style={{ fontWeight: 600, color: "#111827" }}>{r.client?.name || "—"}</div>
                                {r.visit?.visit_number ? <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.visit.visit_number}</div> : null}
                              </td>
                              <td style={{ padding: "8px 10px", color: "#374151" }}>{r.payment_method || "—"}</td>
                              <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: "#111827" }}>
                                {rupiah.format(Number(r.amount_received || 0))}
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                <button
                                  className="button-link"
                                  style={{ color: "#2563eb", fontWeight: 600, fontSize: 12, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                                  onClick={() => openDialog("payment", { id: r.id })}
                                >
                                  Invoice
                                </button>
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                {r.visit_id ? (
                                  <button
                                    style={{
                                      background: "none", border: "none", cursor: surveyLoadingId === r.visit_id ? "wait" : "pointer",
                                      fontSize: 18, padding: 0, opacity: surveyLoadingId && surveyLoadingId !== r.visit_id ? 0.4 : 1,
                                    }}
                                    title="Kirim Survey WA"
                                    disabled={!!surveyLoadingId}
                                    onClick={() => openSurveyWa(r)}
                                  >
                                    {surveyLoadingId === r.visit_id ? "⏳" : "📲"}
                                  </button>
                                ) : <span style={{ color: "#d1d5db" }}>—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="muted">Tidak ada transaksi lunas untuk periode ini.</p>
                )}
              </>
            );
          })()}
        </section>
      ) : null}
      {dialog?.loading ? (
        <div className="modal-backdrop">
          <div className="modal-loading">
            <div className="loader" />
            <span>Menyiapkan form…</span>
          </div>
        </div>
      ) : null}
      {dialog?.type === "patient" && !dialog.loading && references ? (
        <PatientFormModal
          client={dialog.record}
          references={references}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
        />
      ) : null}
      {dialog?.type === "visit" && !dialog.loading && references ? (
        <VisitFormModal
          references={references}
          activeModules={identity?.activeModules}
          preferredClient={dialog.record?.prefillClient || null}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
        />
      ) : null}
      {dialog?.type === "examination" && !dialog.loading && examinationData ? (
        <ExaminationFormModal
          data={examinationData}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
          onGoToAction={() => openDialog("action", dialog.record)}
        />
      ) : null}
      {dialog?.type === "estetik" && !dialog.loading && estetikData ? (
        <EstetikFormModal
          data={estetikData}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
          onGoToAction={() => openDialog("action", dialog.record)}
        />
      ) : null}
      {dialog?.type === "dental" && !dialog.loading && dentalData ? (
        <DentalFormModal
          data={dentalData}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
          onGoToAction={() => openDialog("action", dialog.record)}
        />
      ) : null}
      {dialog?.type === "dokter_umum" && !dialog.loading && dokterUmumData ? (
        <DokterUmumFormModal
          data={dokterUmumData}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
          onGoToAction={() => openDialog("action", dialog.record)}
        />
      ) : null}
      {dialog?.type === "photo" && !dialog.loading ? (
        <PhotoModal
          visit={dialog.record}
          client={dialog.record?.client}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.type === "consent" && !dialog.loading ? (
        <InformedConsentModal
          visit={dialog.record}
          client={dialog.record?.client}
          clinicName={identity?.clinic?.name}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); onChanged(); }}
        />
      ) : null}
      {dialog?.type === "action" && !dialog.loading && actionData ? (
        <ActionFormModal
          data={actionData}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
          onGoToExamination={() => openDialog("examination", dialog.record)}
        />
      ) : null}
      {dialog?.type === "payment" && !dialog.loading && paymentData ? (
        <PaymentFormModal
          data={paymentData}
          canWrite={canWriteBilling}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
        />
      ) : null}
      {dialog?.type === "packagePurchase" &&
      !dialog.loading &&
      packageReferences ? (
        <PackagePurchaseModal
          references={packageReferences}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
        />
      ) : null}
    </div>
  );
}

function ComingSoon({ page }) {
  const item = MENU[page] || { label: page, icon: Settings };
  const Icon = item.icon;
  return (
    <section className="coming-soon panel">
      <div className="coming-icon">
        <Icon size={32} />
      </div>
      <span className="eyebrow">Tahap halaman berikutnya</span>
      <h2>{item.label}</h2>
      <p>
        Menu ini sudah mengikuti hak akses role, tetapi datanya belum dibuka
        pada portal validasi pertama.
      </p>
      <div className="alert alert-info">
        SMC tetap belum disentuh. Pengerjaan diteruskan di tenant XV.
      </div>
    </section>
  );
}

function Portal({ identity, onSignOut, onClinicUpdated }) {
  const allowed = Array.isArray(identity.role.menu_access)
    ? identity.role.menu_access
    : [];
  const initial = allowed.includes("dashboard") ? "dashboard" : allowed[0];
  const [page, setPage] = useState(initial || "dashboard");
  const [regTab, setRegTab] = useState("kunjungan"); // sub-tab untuk Pendaftaran
  const [reservasiPrefill, setReservasiPrefill] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialog, setDialog] = useState(null);

  const canWritePackages = identity.permissions.has("packages.write");
  const canWriteBilling = identity.permissions.has("billing.write");

  async function openDialog(type, record = null) {
    if (type === "packagePurchase") {
      try {
        const refs = await loadPackageReferenceData();
        setDialog({ type, loading: false, record, references: refs });
      } catch (e) {
        setDialog({ type, loading: false, record, error: friendlyError(e) });
      }
    }
  }

  const refresh = useCallback(async () => {
    setRefreshKey((current) => current + 1);
    setLoading(true);
    setError("");
    try {
      const result =
        page === "dashboard"
          ? await loadDashboard(identity)
          : DATA_PAGES.has(page)
              ? await loadPageData(page, search, identity)
            : page === "procurement"
              ? await loadProcurementData()
              : page === "reports"
                ? { refreshKey: Date.now() }
                : [];
      setData(result);
    } catch (loadError) {
      setError(friendlyError(loadError));
    } finally {
      setLoading(false);
    }
  }, [identity, page, search]);

  useEffect(() => {
    const timer = window.setTimeout(refresh, page === "dashboard" ? 0 : 250);
    return () => window.clearTimeout(timer);
  }, [refresh, page]);


  function selectPage(nextPage) {
    if (nextPage === page) return;
    setPage(nextPage);
    setSearch("");
    setData(null);
    if (nextPage !== "registration") { setRegTab("kunjungan"); setReservasiPrefill(null); }
  }

  const title = MENU[page]?.label || "Portal XV";
  return (
    <div className="app-shell">
      <Sidebar
        identity={identity}
        current={page}
        onSelect={selectPage}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="app-main">
        <Topbar
          title={title}
          identity={identity}
          onMenu={() => setSidebarOpen(true)}
          onRefresh={refresh}
          refreshing={loading}
          onSignOut={onSignOut}
        />
        <main className="content">
          {error && (
            <div className="alert alert-danger">
              <strong>Data gagal dimuat.</strong> {error}
            </div>
          )}
          {loading && data === null ? (
            <div className="content-loading">
              <div className="loader" />
              <span>Memuat {title.toLowerCase()}…</span>
            </div>
          ) : null}
          {!loading && !error && page === "dashboard" && data ? (
            <Dashboard data={data} onNavigate={selectPage} onRefresh={refresh} />
          ) : null}
          {!error && page === "medicalRecords" ? (
            <MedicalRecordsPage identity={identity} />
          ) : null}
          {!error && page === "services" ? <MasterTindakanPage /> : null}
          {!error && page === "bhp" ? <MasterBHPPage identity={identity} onChanged={refresh} refreshKey={refreshKey} /> : null}
          {!error && page === "packages" ? (
            <PackagesPage
              canWrite={canWritePackages}
              canWriteBilling={canWriteBilling}
              onDialog={openDialog}
            />
          ) : null}
          {!error && page === "piutang" ? <PiutangPage /> : null}
          {!error && page === "users" ? <PenggunaPage /> : null}
          {!error && page === "payroll" ? <PayrollPage identity={identity} /> : null}
          {!error && page === "attendance" ? <KehadiranPage /> : null}
          {!error && page === "appointment" ? <AppointmentPage identity={identity} /> : null}
          {/* Sub-tab Reservasi dalam Pendaftaran */}
          {!error && page === "registration" && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", borderBottom: "2px solid #e8e2d5", marginBottom: 4 }}>
                <button
                  onClick={() => setRegTab("kunjungan")}
                  style={{
                    background: "none", border: "none", padding: "9px 20px",
                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                    color: regTab === "kunjungan" ? "#2f6f4f" : "#6b6555",
                    borderBottom: regTab === "kunjungan" ? "2px solid #2f6f4f" : "2px solid transparent",
                    marginBottom: -2,
                  }}
                >
                  <CalendarDays size={15} style={{ marginRight: 6, verticalAlign: "middle" }} />
                  Kunjungan
                </button>
                <button
                  onClick={() => setRegTab("reservasi")}
                  style={{
                    background: "none", border: "none", padding: "9px 20px",
                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                    color: regTab === "reservasi" ? "#2f6f4f" : "#6b6555",
                    borderBottom: regTab === "reservasi" ? "2px solid #2f6f4f" : "2px solid transparent",
                    marginBottom: -2,
                  }}
                >
                  <CalendarPlus size={15} style={{ marginRight: 6, verticalAlign: "middle" }} />
                  Reservasi
                </button>
              </div>
              {regTab === "reservasi" && <AppointmentPage identity={identity} prefillClient={reservasiPrefill} />}
            </div>
          )}
          {!error && page === "followup" ? (
            <FollowUpPage
              identity={identity}
              onGoToReservasi={(client) => { selectPage("registration"); setRegTab("reservasi"); setReservasiPrefill(client || null); }}
            />
          ) : null}
          {!error && page === "settings" ? (
            <SettingsPage
              identity={identity}
              onClinicUpdated={onClinicUpdated}
            />
          ) : null}
          {!error &&
          page !== "dashboard" &&
          page !== "memberMgm" &&
          page !== "medicalRecords" &&
          page !== "services" &&
          page !== "bhp" &&
          page !== "followup" &&
          !(page === "registration" && regTab === "reservasi") &&
          LIVE_PAGES.has(page) &&
          Array.isArray(data) ? (
            <CollectionPage
              page={page}
              rows={data}
              search={search}
              onSearch={setSearch}
              identity={identity}
              onChanged={refresh}
              onNavigate={selectPage}
            />
          ) : null}
          {!error && page === "reports" && data ? (
            <DailyClosingPage
              identity={identity}
              refreshKey={data.refreshKey}
            />
          ) : null}
          {!error && page === "procurement" && data ? (
            <ProcurementPage
              data={data}
              identity={identity}
              onChanged={refresh}
            />
          ) : null}
          {!error && page === "survey" ? <SurveyDashboard /> : null}
          {!error && page === "voucher" ? <VoucherClaimPage /> : null}
          {!error && page === "memberMgm" ? <PageErrorBoundary><MembershipPage /></PageErrorBoundary> : null}
          {!error && page === "farmasi" ? (
            <FarmasiPage identity={identity} />
          ) : null}
          {!FUNCTIONAL_PAGES.has(page) ? <ComingSoon page={page} /> : null}
        </main>
      </div>
      {dialog?.type === "packagePurchase" && !dialog.loading && dialog.references ? (
        <PackagePurchaseModal
          references={dialog.references}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); refresh(); }}
        />
      ) : null}
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [authAction, setAuthAction] = useState(null);
  const [booting, setBooting] = useState(true);
  const [identityError, setIdentityError] = useState("");

  const initialize = useCallback(async (nextSession) => {
    setSession(nextSession);
    setIdentity(null);
    setIdentityError("");
    if (!nextSession) {
      setAuthAction(null);
      setBooting(false);
      return;
    }
    const action = authActionFromLocation();
    if (action) {
      setAuthAction(action);
      setBooting(false);
      return;
    }
    setAuthAction(null);
    setBooting(true);
    try {
      const id = await resolveIdentity(nextSession.user);
      // Apply saved appearance on login
      if (id?.clinic) {
        import('./data.js').then(({ loadClinicSettings }) =>
          loadClinicSettings().then((data) => {
            if (data?.contact?.appearance) applyAppearance(data.contact.appearance);
          }).catch(() => {})
        );
      }
      setIdentity(id);
      // Auto check-in
      if (id?.staffMember) {
        recordCheckIn(id.staffMember).catch(() => {});
      }
    } catch (error) {
      setIdentityError(friendlyError(error));
      await supabase?.auth.signOut();
      setSession(null);
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => {
    if (!supabase) {
      setBooting(false);
      return undefined;
    }
    supabase.auth.getSession().then(({ data }) => initialize(data.session));
    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (event === "PASSWORD_RECOVERY") {
          setSession(nextSession);
          setIdentity(null);
          setAuthAction("recovery");
          setBooting(false);
          return;
        }
        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
          if (nextSession?.user?.id !== session?.user?.id)
            initialize(nextSession);
        }
        if (event === "SIGNED_OUT") initialize(null);
      },
    );
    return () => listener.subscription.unsubscribe();
  }, [initialize, session?.user?.id]);

  async function signOut() {
    // Auto check-out
    if (identity?.staffMember) {
      await recordCheckOut(identity.staffMember).catch(() => {});
    }
    await supabase?.auth.signOut();
    setIdentity(null);
    setSession(null);
    setAuthAction(null);
  }

  // Survey page — akses publik tanpa login
  const params = new URLSearchParams(window.location.search);
  const surveyToken = params.get("survey");
  if (surveyToken) return <SurveyPage token={surveyToken} />;
  if (params.get("patient") === "login") return <PatientPortalPage />;

  if (authAction && session) {
    return (
      <PasswordSetup
        session={session}
        action={authAction}
        onComplete={(nextSession) => {
          setAuthAction(null);
          initialize(nextSession);
        }}
        onSignOut={signOut}
      />
    );
  }

  if (booting)
    return <LoadingScreen label="Memeriksa akun dan akses tenant XV…" />;
  if (!session || !identity)
    return (
      <>
        <Login onSignedIn={initialize} />
        {identityError && <div className="floating-error">{identityError}</div>}
      </>
    );
  return (
    <Portal
      identity={identity}
      onSignOut={signOut}
      onClinicUpdated={(updated) =>
        setIdentity((prev) => prev ? { ...prev, clinic: { ...prev.clinic, ...updated } } : prev)
      }
    />
  );
}
